// Tenantra Inc. - Admin Login Page with Ivory Theme
import { useState } from 'react';
import { Shield, Eye, EyeOff, AlertCircle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api from '@/services/api';
import { setTokens, setUser } from '@/services/auth';
import MFALogin from '@/components/MFALogin';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaMethod, setMfaMethod] = useState('');
  const [phoneLastFour, setPhoneLastFour] = useState('');
  const [emailMasked, setEmailMasked] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      const data = response.data;

      // Check if MFA is required
      if (data.mfa_required) {
        setMfaRequired(true);
        setMfaToken(data.mfa_token);
        setMfaMethod(data.mfa_method);
        setPhoneLastFour(data.phone_last_four || '');
        setEmailMasked(data.email_masked || '');
        setLoading(false);
        return;
      }

      // No MFA — verify admin role
      if (data.user?.role !== 'admin') {
        setError('Access denied. Admin credentials required.');
        setLoading(false);
        return;
      }
      
      setTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      window.location.href = '/portal-8k2m9x/dashboard';
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleMFASuccess = (data) => {
    if (data.user?.role !== 'admin') {
      setError('Access denied. Admin credentials required.');
      setMfaRequired(false);
      return;
    }
    localStorage.setItem('tenantra_access_token', data.token);
    localStorage.setItem('tenantra_user', JSON.stringify(data.user));
    window.location.href = '/portal-8k2m9x/dashboard';
  };

  const handleMFACancel = () => {
    setMfaRequired(false);
    setMfaToken('');
    setMfaMethod('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-[#F7F9FA] flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-64 sm:w-96 h-64 sm:h-96 bg-violet-300/20 rounded-full blur-[100px] sm:blur-[128px]" />
        <div className="absolute bottom-1/4 -right-32 w-64 sm:w-96 h-64 sm:h-96 bg-purple-300/20 rounded-full blur-[100px] sm:blur-[128px]" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-500/25 mb-4">
            <Shield className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Tenantra Admin</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Administrative Console</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-xl">
          {mfaRequired ? (
            <MFALogin
              mfaToken={mfaToken}
              mfaMethod={mfaMethod}
              phoneLastFour={phoneLastFour}
              emailMasked={emailMasked}
              onSuccess={handleMFASuccess}
              onCancel={handleMFACancel}
            />
          ) : (
            <>
              <div className="mb-5 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-slate-800 mb-1">Admin Sign In</h2>
                <p className="text-slate-500 text-xs sm:text-sm">Authorized personnel only</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                {error && (
                  <Alert className="bg-red-50 border-red-200 text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-sm">{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 text-sm">Admin Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@tenantra.ca"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11 sm:h-12 bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-violet-500 focus:ring-violet-500/20"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700 text-sm">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter admin password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11 sm:h-12 bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-violet-500 focus:ring-violet-500/20 pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 sm:h-12 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold shadow-lg shadow-violet-500/25 border-0"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Authenticating...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Lock className="w-5 h-5" />
                      Access Admin Console
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-5 sm:mt-6 p-3 sm:p-4 bg-violet-50 border border-violet-200 rounded-xl">
                <p className="text-violet-700 text-xs sm:text-sm flex items-start gap-2">
                  <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  This is a restricted area. All access attempts are logged and monitored.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="mt-5 sm:mt-6 text-center space-y-2">
          <a href="/login" className="text-xs sm:text-sm text-slate-500 hover:text-slate-700 transition-colors">
            &larr; Back to Landlord Login
          </a>
          <p className="text-xs text-slate-400">
            &copy; {new Date().getFullYear()} Tenantra Inc. Admin Portal v1.0
          </p>
        </div>
      </div>
    </div>
  );
}
